import React, { useState, useEffect } from 'react'
import { Checkbox, MenuItem, Position } from '@blueprintjs/core'
import { Select2, ItemListPredicate } from '@blueprintjs/select'
import { Flex } from '@ui'
import Button, { IUIButtonProps } from '../buttons/Button'
import fuzzy from 'fuzzaldrin-plus'
import api from '@api'

interface CitySelectProps {
  useAll: boolean
  value: React.ReactNode | null
  onChange: (city: string) => void
  buttonProps?: IUIButtonProps
  citySelected?: (city: string) => boolean
  includeOldBuildings?: boolean
  multi?: boolean
  btnText?: React.ReactNode
}

const CitySelect = Select2<string>

export default function CitySelectWrapper({
  useAll,
  value,
  buttonProps,
  onChange,
  multi,
  citySelected,
  includeOldBuildings,
}: CitySelectProps) {
  const cities = useCities(useAll, includeOldBuildings)

  return (
    <CitySelect
      resetOnSelect
      disabled={!cities.length}
      inputProps={{ placeholder: 'Search...' }}
      popoverProps={{
        minimal: true,
        position: Position.BOTTOM,
      }}
      noResults={<MenuItem disabled text="No results" />}
      items={cities}
      itemRenderer={(city, { handleClick, modifiers, query }) => {
        if (!modifiers.matchesPredicate) return null
        return (
          <MenuItem
            active={modifiers.active}
            disabled={modifiers.disabled}
            key={city}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              if (multi) {
                e.preventDefault()
                onChange(city)
                return
              }
              handleClick(e)
            }}
            text={
              <Flex align="center">
                {multi && <Checkbox checked={citySelected?.(city)} />}
                {formatQuery(city, query)}
              </Flex>
            }
          />
        )
      }}
      itemListPredicate={filterCities}
      onItemSelect={onChange}
    >
      <Button text={value || 'All Cities'} disabled={!cities.length} {...buttonProps} />
    </CitySelect>
  )
}

function formatQuery(city: string, query: string) {
  if (!query) return city
  return (
    <span
      dangerouslySetInnerHTML={{
        __html: fuzzy.wrap(city, query),
      }}
    />
  )
}

const filterCities: ItemListPredicate<string> = (query, cities) => {
  return query ? fuzzy.filter(cities, query) : cities
}

let persistedState: string[] = []

export function useCities(useAll: boolean, includeOldBuildings?: boolean): string[] {
  const [cities, setCities] = useState(persistedState)

  useEffect(() => {
    api.city
      .list(includeOldBuildings)
      .then((projection_cities) =>
        useAll ? setCities(['All Cities', ...projection_cities]) : setCities(projection_cities)
      )
  }, [useAll, includeOldBuildings])

  useEffect(() => {
    persistedState = cities
  })

  return cities
}
